<script setup>
import { defineAsyncComponent, ref } from "vue";
const { isDesktop } = useDevice();

const props = defineProps({
  oldVersion: {
    type: Array,
    default: () => [
      { title: "Laravel", version: "4.2", background: "bg-[#48169E]" },
      { title: "Laravel", version: "5.0", background: "bg-[#581FBA]" },
      { title: "Laravel", version: "5.1", background: "bg-[#7138D3]" },
    ],
  },
  newVersion: {
    type: Array,
    default: () => [
      { title: "Laravel", version: "12.x", background: "bg-[#8B38D3]" },
      { title: "Laravel", version: "13.x", background: "bg-[#FF2D20]" },
    ],
  },
  preVer: {
    type: Array,
    default: () => [
      { label: "Laravel 5" },
      { label: "Laravel 5.1" },
      { label: "Laravel 5.2" },
      { label: "Laravel 5.3" },
      { label: "Laravel 5.4" },
      { label: "Laravel 5.5" },
      { label: "Laravel 5.6" },
      { label: "Laravel 5.7" },
      { label: "Laravel 5.8" },
      { label: "Laravel 6" },
      { label: "Laravel 7" },
      { label: "Laravel 8" },
    ],
  },
  latestVer: {
    type: Array,
    default: () => [
      { label: "Laravel 9" },
      { label: "Laravel 10" },
      { label: "Laravel 11" },
    ],
  },
  formTitleFrom: {
    type: String,
    default: "Upgrading From",
  },
  formTitleTo: {
    type: String,
    default: "Upgrading To",
  },
  onSubmit: {
    type: Function,
    default: (data) => {
      console.log("Submitted Data:", data);
    },
  },
  popupComponent: {
    type: Object,
    default: () =>
      defineAsyncComponent(() => import("./WrContactFormPopup.vue")),
  },
});

const emailPattern =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,})$/i;

const errors = ref({
  name: "",
  email: "",
  mobile: "",
  service: "",
  description: "",
});

const visible = ref(false);
const formPrefilledData = ref(null);

const formData = ref({
  selectedUpgradeFrom: null,
  selectedUpgradeTo: null,
  businessEmail: null,
});

const validateEmail = () => {
  errors.value.email = "";
  if (!formData.value.businessEmail) {
    errors.value.email = "Email is required";
    return false;
  }
  if (!emailPattern.test(formData.value.businessEmail.toLowerCase())) {
    errors.value.email = "Invalid email format";
    return false;
  }
  return true;
};

const openModal = (prefilledValue = null) => {
  formPrefilledData.value = prefilledValue?.length ? prefilledValue : null;
  visible.value = true;
};

const submitForm = () => {
  if (!validateEmail()) return;
  props.onSubmit(formData.value);
  openModal([formData.value]);
};
</script>

<template>
  <div
    class="flex max-sm:!gap-4 max-xl:gap-7 gap-y-7 max-lg:flex-wrap w-full items-center justify-center"
  >
    <div
      v-for="version in oldVersion"
      :key="version.version"
      :class="version.background"
      class="xl:ml-7 first:ml-0 text-white font-medium max-w-[194px] text-2xl md:text-3xl px-9 py-7 rounded-lg text-center"
    >
      {{ version.title }} <br />
      <span class="text-4xl md:text-5xl">{{ version.version }}</span>
    </div>

    <div class="grow border-0 border-t-4 border-dashed border-primary"></div>

    <div
      v-for="version in newVersion"
      :key="version.version"
      :class="version.background"
      class="xl:mr-7 last:mr-0 text-white font-medium max-w-[194px] text-2xl md:text-3xl px-9 py-7 rounded-lg text-center"
    >
      {{ version.title }} <br />
      <span class="text-4xl md:text-5xl">{{ version.version }}</span>
    </div>
  </div>

  <form
    @submit.prevent="submitForm"
    class="mt-20 flex max-md:flex-wrap items-end gap-6 max-w-[1024px] mx-auto"
  >
    <div class="w-full">
      <p class="mb-2.5">{{ formTitleFrom }}</p>
      <div
        class="p-px dark:bg-[image:var(--border-form-card)] bg-none bg-[#B5B5B5] rounded-lg"
      >
        <Select
          v-model="formData.selectedUpgradeFrom"
          :options="preVer"
          optionLabel="label"
          placeholder="Select a version"
          class="w-full rounded-lg border-0"
        />
      </div>
    </div>

    <div class="w-full">
      <p class="mb-2.5">{{ formTitleTo }}</p>
      <div
        class="p-px dark:bg-[image:var(--border-form-card)] bg-none bg-[#B5B5B5] rounded-lg"
      >
        <Select
          v-model="formData.selectedUpgradeTo"
          :options="latestVer"
          optionLabel="label"
          placeholder="Select a version"
          class="w-full rounded-lg border-0"
        />
      </div>
    </div>

    <div class="w-full">
      <p class="mb-2.5">Business Email</p>

      <!-- make THIS wrapper the positioning context -->
      <div class="relative overflow-visible">
        <div
          class="p-px dark:bg-[image:var(--border-form-card)] bg-none bg-[#B5B5B5] rounded-lg"
        >
          <InputText
            placeholder="Business Email address *"
            class="w-full rounded-lg border-0"
            v-model="formData.businessEmail"
            :class="{ 'p-invalid': errors.email }"
            @blur="validateEmail"
            @input="errors.email = ''"
          />
        </div>

        <!-- sits just under the input, without shifting layout -->
        <small
          v-if="errors.email"
          class="z-50 absolute left-0 top-full translate-y-1 text-red-600 text-xs pointer-events-none"
        >
          {{ errors.email }}
        </small>
      </div>
    </div>

    <WrButton @click="submitForm" container-class="min-w-max"
      >Upgrade now</WrButton
    >
  </form>

    <Teleport to="body">
      <component
        :is="popupComponent"
        v-if="visible"
        :isOpen="visible"
        @close="visible = false"
        :prefilledContent="formPrefilledData"
        class="z-30"
      />
    </Teleport>
</template>
